import React, { FunctionComponent, useState } from "react";
import { Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from "@mui/material";
import { Delete, Edit, Event } from "@mui/icons-material";

import WorkshopDialog from "./WorkshopDialog";
import useResponsive from "../../hooks/useResponsive";
import useWorkshops from "../../hooks/workshops/useWorkshops";
import { CreateWorkshopDto, UpdateWorkshopDto } from "../../types/workshopTypes";

interface WorkshopButtonProps {
  edit?: boolean;
  deletion?: boolean;
  workshopId?: number;
  workshopName?: string;
  workshopDescription?: string;
  workshopType?: "Pflichtworkshop" | "Workshop" | "Externer Workshop";
}

/**
 * Button to add a new workshop and open the corresponding dialog
 * @returns Button to add a new workshop and open the corresponding dialog
 */
const WorkshopButton: FunctionComponent<WorkshopButtonProps> = ({
  edit,
  deletion,
  workshopId,
  workshopName,
  workshopDescription,
  workshopType,
}) => {
  const [addWorkshopDialogOpen, setAddWorkshopDialogOpen] = useState<boolean>(false);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState<boolean>(false);

  const isMobile = useResponsive("down", "sm");

  // React Query hooks
  const { createWorkshop, updateWorkshop, deleteWorkshop } = useWorkshops();

  // Function to open the dialog
  const handleDialogOpen = () => {
    setAddWorkshopDialogOpen(true);
  };

  // Function to close the dialog
  const handleDialogClose = () => {
    setAddWorkshopDialogOpen(false);
  };

  // Function to save the workshop
  const handleSave = async (
    name: string,
    description: string,
    type: "Pflichtworkshop" | "Workshop" | "Externer Workshop"
  ) => {
    if (edit && workshopId) {
      // Update existing workshop
      const updateData: UpdateWorkshopDto = {
        name,
        description,
        type,
      };
      await updateWorkshop(workshopId, updateData);
    } else {
      // Create new workshop
      const createData: CreateWorkshopDto = {
        name,
        description,
        type,
      };
      await createWorkshop(createData);
    }
  };

  // Function to handle workshop deletion
  const handleDelete = async () => {
    if (!workshopId) return;
    try {
      await deleteWorkshop(workshopId);
    } catch {
      // toast already shown by hook
    } finally {
      setDeleteConfirmDialogOpen(false);
    }
  };

  // Function to open delete confirmation dialog
  const handleDeleteClick = () => {
    setDeleteConfirmDialogOpen(true);
  };

  // Function to close delete confirmation dialog
  const handleDeleteCancel = () => {
    setDeleteConfirmDialogOpen(false);
  };

  if (deletion) {
    return (
      <>
        {isMobile ? (
          <IconButton onClick={handleDeleteClick}>
            <Delete color="error" />
          </IconButton>
        ) : (
          <Button
            variant="contained"
            color="error"
            sx={{ fontWeight: 600, mr: 2 }}
            onClick={handleDeleteClick}
            startIcon={<Delete />}
          >
            Workshop löschen
          </Button>
        )}
        <Dialog open={deleteConfirmDialogOpen} onClose={handleDeleteCancel}>
          <DialogTitle>Workshop löschen</DialogTitle>
          <DialogContent>
            <Typography>
              Bist du sicher, dass du den Workshop "{workshopName}" löschen möchtest? Diese Aktion kann nicht rückgängig
              gemacht werden.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel}>Abbrechen</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Löschen
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <>
      {isMobile ? (
        <Button
          variant="contained"
          onClick={handleDialogOpen}
          startIcon={edit ? <Edit /> : <Event />}
          sx={{ fontSize: 10 }}
          color={edit ? "primary" : "info"}
        >
          {edit ? "bearbeiten" : "neu"}
        </Button>
      ) : (
        <Button
          variant="contained"
          startIcon={edit ? <Edit /> : <Event />}
          color={edit ? "primary" : "info"}
          sx={{ fontWeight: 600 }}
          onClick={handleDialogOpen}
          size="small"
        >
          {edit ? "bearbeiten" : "neu"}
        </Button>
      )}
      <WorkshopDialog
        open={addWorkshopDialogOpen}
        onClose={handleDialogClose}
        onSave={handleSave}
        workshopName={workshopName}
        workshopDescription={workshopDescription}
        workshopType={workshopType}
        edit={edit}
      />
    </>
  );
};

export default WorkshopButton;
